/**
 * Inventory Value Report Page
 */

import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Package, DollarSign, AlertTriangle, Boxes } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface InventoryValueData {
  total_articles: number
  total_units: number
  total_cost_value: number
  total_sale_value: number
  low_stock_count: number
}

export function InventoryValueReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data: inventoryValue, isLoading } = useQuery({
    queryKey: ['reports-inventory-value'],
    queryFn: async () => {
      const response = await api.get('/reports/inventory/value')
      return response.data as InventoryValueData
    },
  })

  const profitMargin = inventoryValue
    ? inventoryValue.total_sale_value - inventoryValue.total_cost_value
    : 0

  const profitPercentage = inventoryValue && inventoryValue.total_cost_value > 0
    ? ((profitMargin / inventoryValue.total_cost_value) * 100).toFixed(1)
    : '0'

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.inventoryValue')}</h1>
          <p className="text-sm text-muted-foreground">{t('reports.inventoryValueDesc')}</p>
        </div>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : inventoryValue ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('reports.totalArticles')}</p>
                    <p className="text-xl font-bold">{inventoryValue.total_articles.toLocaleString('es-CR')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900">
                    <Boxes className="h-5 w-5 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('reports.totalUnits')}</p>
                    <p className="text-xl font-bold">{inventoryValue.total_units.toLocaleString('es-CR')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-2 md:col-span-1 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900">
                    <DollarSign className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('reports.costValue')}</p>
                    <p className="text-lg font-bold text-red-700 dark:text-red-400 tabular-nums break-words">{formatCurrency(inventoryValue.total_cost_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="col-span-2 md:col-span-1 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">{t('reports.saleValue')}</p>
                    <p className="text-lg font-bold text-success tabular-nums break-words">{formatCurrency(inventoryValue.total_sale_value)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profit Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-emerald-500" />
                {t('reports.misc.profitSummary')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <p className="text-sm text-muted-foreground">{t('reports.misc.totalCost')}</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(inventoryValue.total_cost_value)}
                  </p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <p className="text-sm text-muted-foreground">{t('reports.misc.saleValue')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(inventoryValue.total_sale_value)}
                  </p>
                </div>
                <div className="text-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950">
                  <p className="text-sm text-muted-foreground">{t('reports.misc.potentialProfit')}</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(profitMargin)}
                  </p>
                  <p className="text-sm text-emerald-600">({profitPercentage}%)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Low Stock Alert */}
          {inventoryValue.low_stock_count > 0 && (
            <Card className="bg-amber-50 dark:bg-amber-950 border-amber-200 dark:border-amber-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-8 w-8 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      {inventoryValue.low_stock_count} artículos con stock bajo
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('reports.misc.checkZeroStock')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => navigate('/reports/zero-stock')}
                  >
                    {t('reports.misc.viewDetails')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">{t('reports.noDataAvailable')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
